import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import type { PartialBlock } from "@blocknote/core";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";

interface BlockNoteEditorProps {
  value: string;
  onChange: (val: string) => void;
  minHeight?: string;
}

const parseContent = (raw: string): PartialBlock[] | undefined => {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as PartialBlock[];
  } catch (_) {
    return undefined;
  }
};

const BlockNoteEditor: React.FC<BlockNoteEditorProps> = ({ value, onChange, minHeight = "360px" }) => {
  const initialContent = useMemo(() => parseContent(value), []);
  const editor = useCreateBlockNote({ initialContent });
  const lastValueRef = useRef<string | undefined>(value);

  // When parent provides a new value (e.g., row click), sync editor content.
  useEffect(() => {
    if (!editor) return;
    if (value === lastValueRef.current) return;
    const parsed = parseContent(value) ?? [];
    const blocksToInsert = parsed.length ? parsed : [{ type: "paragraph", content: [] } as PartialBlock];
    editor.replaceBlocks(editor.document, blocksToInsert);
    lastValueRef.current = value;
  }, [editor, value]);

  const handleChange = useCallback(() => {
    if (editor) {
      const json = JSON.stringify(editor.document);
      lastValueRef.current = json;
      onChange(json);
    }
  }, [editor, onChange]);

  return (
    <div className="border border-surface-border rounded-2xl bg-white shadow-card" style={{ minHeight }}>
      <BlockNoteView editor={editor} theme="light" onChange={handleChange} />
    </div>
  );
};

export default BlockNoteEditor;
